import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import {
  adjustProductStock,
  deactivateProduct,
  getProduct,
  listPriceLists,
  updateProduct,
  upsertPriceListItems,
} from "@/shared/api/inventory";
import { redeemOverrideOtp } from "@/shared/api/overrides";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { AppSheet } from "@/modules/shell/AppSheet";
import { DetailReadout } from "@/modules/shell/DetailReadout";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  DETAIL_COMPACT_MQ,
  discardChangesConfirm,
  isDetailDraftDirty,
  NO_PERMISSION_EDIT,
} from "@/modules/shell/detail-edit-mode";
import { useCompanyHeld } from "@/modules/shell/use-company-held";
import { useDetailEditMode } from "@/modules/shell/use-detail-edit-mode";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { notify } from "@/shared/feedback/notify";
import { RedactedMoney } from "@/shared/feedback/redacted-value";
import { productBalanceColumns } from "./product-detail-columns";

function isStockInDelta(raw: string): boolean {
  const n = Number(String(raw).trim().replace(/,/g, ""));
  return Number.isFinite(n) && n > 0;
}

function pricesFromProduct(
  prices: { priceListId: string; price: string }[],
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const row of prices) next[row.priceListId] = row.price;
  return next;
}

function pricesDirty(
  drafts: Record<string, string>,
  server: Record<string, string>,
  listIds: string[],
): boolean {
  for (const id of listIds) {
    const a = (drafts[id] ?? "").trim();
    const b = (server[id] ?? "").trim();
    if (a !== b) return true;
  }
  return false;
}

export function ProductDetailPage() {
  const { companySlug, productId } = useParams();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const adjustSectionRef = useRef<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qtyDelta, setQtyDelta] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [memo, setMemo] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [pendingChangeId, setPendingChangeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [kind, setKind] = useState<"stocked" | "service">("stocked");
  const [trackLot, setTrackLot] = useState(false);
  const [trackSerial, setTrackSerial] = useState(false);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [pendingPriceChangeIds, setPendingPriceChangeIds] = useState<
    string[]
  >([]);
  const [priceOtp, setPriceOtp] = useState("");
  const [pricesOpen, setPricesOpen] = useState(false);
  const [compactOverlay, setCompactOverlay] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DETAIL_COMPACT_MQ).matches
      : true,
  );

  const held = useCompanyHeld(companySlug);
  const canWrite = held.can("inventory.write");

  const productQuery = useQuery({
    queryKey: queryKeys.products.detail(companySlug ?? "", productId ?? ""),
    enabled: Boolean(companySlug && productId),
    queryFn: () => getProduct(companySlug!, productId!),
  });
  const listsQuery = useQuery({
    queryKey: queryKeys.priceLists.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listPriceLists(companySlug!),
  });
  const errorMsg = queryErrorMessage(productQuery.error, productQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const data = productQuery.data ?? null;
  const priceLists = (listsQuery.data?.priceLists ?? []).filter(
    (l) => l.deactivatedAt == null,
  );

  const syncMasterFromData = useCallback(() => {
    if (!data) return;
    setName(data.product.name);
    setSku(data.product.sku ?? "");
    setBarcode(data.product.barcode ?? "");
    setKind(data.product.kind);
    setTrackLot(data.product.trackLot);
    setTrackSerial(data.product.trackSerial);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    syncMasterFromData();
    setUnitCost((prev) => {
      if (prev) return prev;
      if (data.product.avgCost && Number(data.product.avgCost) > 0) {
        return data.product.avgCost;
      }
      return prev;
    });
    setPriceDrafts(pricesFromProduct(data.prices));
  }, [data, syncMasterFromData]);

  useEffect(() => {
    const mq = window.matchMedia(DETAIL_COMPACT_MQ);
    const onChange = () => setCompactOverlay(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const serverMaster = data
    ? {
        name: data.product.name,
        sku: data.product.sku ?? "",
        barcode: data.product.barcode ?? "",
        kind: data.product.kind,
        trackLot: data.product.trackLot,
        trackSerial: data.product.trackSerial,
      }
    : null;
  const draftMaster = {
    name,
    sku,
    barcode,
    kind,
    trackLot,
    trackSerial,
  };
  const masterDirty = serverMaster
    ? isDetailDraftDirty(draftMaster, serverMaster)
    : false;

  const serverPrices = data ? pricesFromProduct(data.prices) : {};
  const priceListIds = priceLists.map((l) => l.id);
  const sheetPricesDirty = pricesDirty(
    priceDrafts,
    serverPrices,
    priceListIds,
  );

  const editBlockedReason = !canWrite ? NO_PERMISSION_EDIT : null;

  const edit = useDetailEditMode({
    dirty: masterDirty,
    canEdit: canWrite,
    editBlockedReason,
    objectLabel: data?.product.name ?? "Product",
    confirm: confirmUi,
    onResetDrafts: syncMasterFromData,
    escEnabled: !pricesOpen,
  });

  async function reload() {
    if (!companySlug || !productId) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.products.detail(companySlug, productId),
    });
  }

  if (!companySlug || !productId) return null;

  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  if (displayError && !data) {
    return (
      <Alert variant="destructive" className="max-w-3xl">
        {displayError}
      </Alert>
    );
  }
  if (productQuery.isPending || !data) {
    return (
      <Alert variant="muted" className="max-w-3xl">
        Loading product…
      </Alert>
    );
  }

  const p = data.product;
  const stocked = p.kind === "stocked";
  const deactivated = p.deactivatedAt != null;

  async function onUpdateMaster(e: React.FormEvent) {
    e.preventDefault();
    if (!masterDirty) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await updateProduct(companySlug!, productId!, {
        name: name.trim(),
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        kind,
        trackLot: kind === "stocked" ? trackLot : false,
        trackSerial: kind === "stocked" ? trackSerial : false,
      });
      setStatus("Updated product");
      notify.updated(name.trim() || "product");
      await reload();
      edit.markSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Update failed";
      setError(msg);
      notify.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function closePricesOverlay() {
    if (sheetPricesDirty) {
      const ok = await confirmUi(discardChangesConfirm());
      if (!ok) return;
      setPriceDrafts(serverPrices);
    }
    setPricesOpen(false);
  }

  async function onSavePrices() {
    const byList = priceLists
      .map((list) => {
        const price = (priceDrafts[list.id] ?? "").trim();
        if (!price) return null;
        const existing = data?.prices.find((r) => r.priceListId === list.id);
        if (existing && existing.price === price) return null;
        return { listId: list.id, price };
      })
      .filter((x): x is { listId: string; price: string } => x != null);
    if (byList.length === 0) {
      setStatus("No price changes");
      setPricesOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    setPendingPriceChangeIds([]);
    try {
      const queued: string[] = [];
      let applied = 0;
      for (const row of byList) {
        const res = await upsertPriceListItems(companySlug!, row.listId, [
          { productId: productId!, price: row.price },
        ]);
        if (res.applied) applied += res.upserted;
        else queued.push(...res.changeRequests.map((r) => r.id));
      }
      setPendingPriceChangeIds(queued);
      if (queued.length > 0) {
        const msg = `${queued.length} price change${queued.length === 1 ? "" : "s"} queued — Approvals or emergency OTP below`;
        setStatus(msg);
        notify.requestSent(
          `${queued.length} price change${queued.length === 1 ? "" : "s"}`,
        );
      } else {
        const msg =
          applied === 0
            ? "No price changes"
            : `Updated ${applied} price${applied === 1 ? "" : "s"}`;
        setStatus(msg);
        if (applied > 0) notify.updated(`${applied} price${applied === 1 ? "" : "s"}`);
        else notify.info(msg);
      }
      setPricesOpen(false);
      await reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Price save failed";
      setError(msg);
      notify.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate() {
    const ok = await confirmUi({
      title: `Deactivate “${p.name}”?`,
      description: "It will hide from new picks. You can reactivate later.",
      confirmLabel: "Deactivate",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await deactivateProduct(companySlug!, productId!);
      setStatus("Product deactivated");
      notify.success("Product deactivated");
      await reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Deactivate failed";
      setError(msg);
      notify.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onReactivate() {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await updateProduct(companySlug!, productId!, { reactivate: true });
      setStatus("Product reactivated");
      notify.success("Product reactivated");
      await reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Reactivate failed";
      setError(msg);
      notify.error(msg);
    } finally {
      setBusy(false);
    }
  }

  function showAdjustError(msg: string) {
    setError(msg);
    notify.error(msg);
    adjustSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  async function onAdjust() {
    setBusy(true);
    setError(null);
    setStatus(null);
    const avgHidden = data?.product.avgCost == null;
    const avgIsZero =
      avgHidden || Number(data?.product.avgCost ?? 0) === 0;
    if (isStockInDelta(qtyDelta) && avgIsZero && !unitCost.trim()) {
      showAdjustError(
        avgHidden
          ? "Enter a unit cost — average cost is hidden for your role"
          : "Enter a unit cost — average cost is still 0 on this product",
      );
      setBusy(false);
      return;
    }
    /** One key for soft-confirm retries so a flaky retry cannot double-adjust — plan/24 */
    const idempotencyKey = `adj-${crypto.randomUUID()}`;
    const payload = {
      qtyDelta: qtyDelta.trim(),
      idempotencyKey,
      ...(warehouseId ? { warehouseId } : {}),
      ...(unitCost.trim() ? { unitCost: unitCost.trim() } : {}),
      ...(memo.trim() ? { memo: memo.trim() } : {}),
    };
    try {
      const res = await adjustProductStock(companySlug!, productId!, payload);
      if (!res.applied) {
        const msg =
          "Cost-changing stock-in queued — Approvals or emergency OTP below";
        setPendingChangeId(res.changeRequest.id);
        setStatus(msg);
        notify.info(msg);
        adjustSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        return;
      }
      setPendingChangeId(null);
      setQtyDelta("");
      setMemo("");
      const msg = res.adjustment.oversell
        ? `Adjusted ${res.adjustment.qtyDelta} · on hand now ${res.adjustment.qtyAfter} (below prior on-hand)`
        : `Adjusted ${res.adjustment.qtyDelta} · on hand now ${res.adjustment.qtyAfter}`;
      setStatus(msg);
      notify.success(msg);
      await reload();
    } catch (err: unknown) {
      showAdjustError(err instanceof Error ? err.message : "Adjust failed");
    } finally {
      setBusy(false);
    }
  }

  function openPricesEditor() {
    const product = data;
    if (!canWrite || deactivated || !product) return;
    setPriceDrafts(pricesFromProduct(product.prices));
    setPricesOpen(true);
  }

  const trackingLabel =
    stocked && (p.trackLot || p.trackSerial)
      ? [p.trackLot ? "Lot" : null, p.trackSerial ? "Serial" : null]
          .filter(Boolean)
          .join(" · ")
      : stocked
        ? "Off"
        : "—";

  const priceEditor = (
    <div className="space-y-3">
      {priceLists.map((list) => (
        <label
          key={list.id}
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          <span className="min-w-[8rem] flex-1">
            {list.name}
            {list.isDefaultPos ? (
              <span className="text-xs text-[var(--color-muted)]">
                {" "}
                · POS default
              </span>
            ) : null}
          </span>
          <Input
            className="w-32 font-mono"
            inputMode="decimal"
            value={priceDrafts[list.id] ?? ""}
            onChange={(e) =>
              setPriceDrafts((prev) => ({
                ...prev,
                [list.id]: e.target.value,
              }))
            }
            placeholder="0.00"
            disabled={busy}
          />
        </label>
      ))}
    </div>
  );

  return (
    <PageFrame>
      <PageHeader
        eyebrow={
          edit.isEditing ? (
            <span>
              Product · <span className="text-[var(--color-foreground)]">Editing</span>
            </span>
          ) : (
            "Product"
          )
        }
        title={p.name}
        description={[
          p.kind === "stocked" ? "Stocked" : "Service",
          p.sku ? `SKU ${p.sku}` : null,
          p.barcode ? p.barcode : null,
          deactivated ? "deactivated" : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <>
            {edit.isEditing ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => void edit.leaveEdit()}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="product-master-form"
                  size="sm"
                  pending={busy}
                  pendingLabel="Updating…"
                  disabled={!masterDirty || !name.trim()}
                >
                  Update
                </Button>
              </>
            ) : (
              <span title={edit.editTitle}>
                <Button
                  type="button"
                  size="sm"
                  disabled={!canWrite || busy}
                  onClick={() => edit.enterEdit()}
                >
                  Edit
                </Button>
              </span>
            )}
            {stocked && (p.trackLot || p.trackSerial) && (
              <Button variant="outline" asChild size="sm">
                <Link
                  to={`/c/${companySlug}/products/${productId}/lots`}
                  className="no-underline"
                >
                  Lot / serial
                </Link>
              </Button>
            )}
            {deactivated ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || edit.isEditing}
                onClick={() => void onReactivate()}
              >
                Reactivate
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy || edit.isEditing}
                onClick={() => void onDeactivate()}
              >
                Deactivate
              </Button>
            )}
          </>
        }
      />
      {edit.showEditBlockedHint ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          {edit.editBlockedReason}
        </p>
      ) : null}

      {edit.isEditing ? (
        <div className="sticky z-10 -mx-1 mt-3 rounded-lg border border-[var(--color-border)]/80 bg-[var(--color-shell)]/95 px-3 py-2 text-xs text-[var(--color-muted)] backdrop-blur supports-[backdrop-filter]:bg-[var(--color-shell)]/80"
          style={{ top: "var(--desk-sticky-offset, 0px)" }}
        >
          Editing · {name.trim() || p.name}
          {sku.trim() ? ` · SKU ${sku.trim()}` : ""}
        </div>
      ) : null}

      <TagsAssigneesPanel
        companySlug={companySlug}
        docType="product"
        docId={productId}
        linkPath={`/c/${companySlug}/products/${productId}`}
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      {status && (
        <p className="mt-4 text-sm text-[var(--color-muted)]">{status}</p>
      )}

      <section className="mt-8 border-b border-[var(--color-border)] pb-8">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Details
        </h2>
        {edit.isEditing ? (
          <form
            id="product-master-form"
            className="mt-4 space-y-3"
            onSubmit={(e) => void onUpdateMaster(e)}
          >
            <label className="block text-sm">
              <span className="text-[var(--color-muted)]">Name</span>
              <Input
                ref={edit.firstFieldRef}
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={busy}
              />
            </label>
            <label className="block text-sm">
              <span className="text-[var(--color-muted)]">Kind</span>
              <Select
                className="mt-1"
                value={kind}
                onChange={(e) =>
                  setKind(e.target.value as "stocked" | "service")
                }
                disabled={busy}
              >
                <option value="stocked">Stocked</option>
                <option value="service">Service (non-stock)</option>
              </Select>
            </label>
            <label className="block text-sm">
              <span className="text-[var(--color-muted)]">SKU (optional)</span>
              <Input
                className="mt-1 font-mono"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="block text-sm">
              <span className="text-[var(--color-muted)]">Barcode (optional)</span>
              <Input
                className="mt-1 font-mono"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                disabled={busy}
              />
            </label>
            {kind === "stocked" ? (
              <AdvancedSection title="Advanced" subtitle="tracking">
                <p className="m-0 text-xs text-[var(--color-muted)]">
                  When enabled, receive and POS require lot and/or serial on each
                  move — plan/31.
                </p>
                <div className="space-y-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={trackLot}
                      onChange={(e) => setTrackLot(e.target.checked)}
                      disabled={busy}
                    />
                    Track lot numbers
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={trackSerial}
                      onChange={(e) => setTrackSerial(e.target.checked)}
                      disabled={busy}
                    />
                    Track serial numbers
                  </label>
                </div>
              </AdvancedSection>
            ) : null}
          </form>
        ) : (
          <DetailReadout
            rows={[
              { label: "Name", value: p.name },
              {
                label: "Kind",
                value: p.kind === "stocked" ? "Stocked" : "Service",
              },
              { label: "SKU", value: p.sku, mono: true },
              { label: "Barcode", value: p.barcode, mono: true },
              ...(stocked
                ? [{ label: "Tracking", value: trackingLabel }]
                : []),
            ]}
          />
        )}
      </section>

      <section className="mt-8 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
              Prices
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 min-h-8 px-2 text-[var(--color-muted)]"
              title={
                !canWrite
                  ? NO_PERMISSION_EDIT
                  : deactivated
                    ? "Reactivate to edit prices"
                    : "Edit prices"
              }
              aria-label="Edit prices"
              disabled={!canWrite || deactivated || busy || listsQuery.isPending}
              onClick={() => openPricesEditor()}
            >
              <Pencil className="size-3.5" aria-hidden />
              <span className="hidden sm:inline">Edit prices</span>
            </Button>
          </div>
          <Link
            to={`/c/${companySlug}/settings/price-lists`}
            className="text-xs text-[var(--color-muted)] no-underline hover:text-[var(--color-foreground)]"
          >
            All price lists
          </Link>
        </div>
        <p className="m-0 text-xs text-[var(--color-muted)]">
          Separate from master Edit — gated save may queue for approval.
        </p>
        {listsQuery.isPending ? (
          <Alert variant="muted">Loading price lists…</Alert>
        ) : priceLists.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            No active price lists.{" "}
            <Link to={`/c/${companySlug}/settings/price-lists`}>
              Create one
            </Link>
            .
          </p>
        ) : (
          <DetailReadout
            className="mt-2"
            rows={priceLists.map((list) => {
              const row = data.prices.find((r) => r.priceListId === list.id);
              return {
                label: list.isDefaultPos
                  ? `${list.name} · POS default`
                  : list.name,
                value: row?.price ?? null,
                mono: true,
              };
            })}
          />
        )}
        {pendingPriceChangeIds.length > 0 && (
          <label className="block text-xs text-[var(--color-muted)]">
            Emergency OTP (applies first queued change)
            <div className="mt-1 flex gap-2">
              <Input
                className="min-w-0 flex-1 font-mono tracking-widest"
                inputMode="numeric"
                placeholder="••••••"
                maxLength={8}
                value={priceOtp}
                onChange={(e) => setPriceOtp(e.target.value)}
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy || priceOtp.trim().length < 4}
                onClick={() => {
                  const requestId = pendingPriceChangeIds[0];
                  if (!requestId) return;
                  setBusy(true);
                  setError(null);
                  void redeemOverrideOtp(companySlug, {
                    code: priceOtp.trim(),
                    requestId,
                    requestKind: "change",
                  })
                    .then(async () => {
                      const rest = pendingPriceChangeIds.slice(1);
                      setPendingPriceChangeIds(rest);
                      setPriceOtp("");
                      setStatus(
                        rest.length === 0
                          ? "OTP accepted — price applied"
                          : `OTP accepted — ${rest.length} still queued`,
                      );
                      await reload();
                    })
                    .catch((err: unknown) =>
                      setError(
                        err instanceof Error ? err.message : "OTP failed",
                      ),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                Use
              </Button>
            </div>
          </label>
        )}
      </section>

      {compactOverlay ? (
        <AppSheet
          open={pricesOpen}
          onOpenChange={(open) => {
            if (!open) void closePricesOverlay();
            else setPricesOpen(true);
          }}
          title="Edit prices"
          description={`Prices for ${p.name}`}
          trailing={
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              pending={busy}
              pendingLabel="…"
              disabled={!sheetPricesDirty || busy}
              onClick={() => void onSavePrices()}
            >
              Update
            </Button>
          }
        >
          {priceEditor}
        </AppSheet>
      ) : (
        <Dialog
          open={pricesOpen}
          onOpenChange={(open) => {
            if (!open) void closePricesOverlay();
            else setPricesOpen(true);
          }}
        >
          <DialogContent className="flex max-h-[min(80dvh,36rem)] w-[min(100%-1.5rem,28rem)] flex-col gap-0 overflow-hidden p-0">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
              <div className="min-w-0">
                <DialogTitle className="text-base">Edit prices</DialogTitle>
                <DialogDescription className="text-xs text-[var(--color-muted)]">
                  {p.name}
                </DialogDescription>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => void closePricesOverlay()}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  pending={busy}
                  pendingLabel="Updating…"
                  disabled={!sheetPricesDirty || busy}
                  onClick={() => void onSavePrices()}
                >
                  Update
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
              {priceEditor}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <section className="mt-6">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Stock
        </h2>
        <p className="mt-2 text-sm">
          Avg cost: <RedactedMoney value={p.avgCost} />
        </p>
        <DataTable
          listId="product-detail-balances"
          companySlug={companySlug}
          className="mt-2"
          columns={productBalanceColumns()}
          data={data.balances}
          getRowId={(r) => r.warehouseCode}
          manualSorting={false}
          empty={
            <EmptyState
              title="No on-hand yet"
              description="Adjust below for opening qty, or receive a PO."
            />
          }
        />
      </section>

      {stocked && (
        <section
          ref={adjustSectionRef}
          className="mt-8 border-t border-[var(--color-border)] pt-6"
        >
          <h2 className="m-0 text-sm font-semibold">Adjust stock</h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Opening count or correction. Posts inventory and updates on-hand.
            Use + for in, − for out. Stock-in at a unit cost different from avg
            is a gated cost change.
          </p>
          {p.avgCost == null ? (
            <Alert variant="muted" className="mt-3">
              Average cost is hidden for your role — enter a unit cost when
              counting stock in.
            </Alert>
          ) : Number(p.avgCost) === 0 ? (
            <Alert variant="muted" className="mt-3">
              Average cost is 0 — enter a unit cost when counting stock in
              (opening / correction).
            </Alert>
          ) : null}
          <form
            className="mt-4 space-y-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void onAdjust();
            }}
          >
            <WarehouseSelect
              companySlug={companySlug}
              value={warehouseId}
              onChange={setWarehouseId}
              disabled={busy}
            />
            <label className="block text-xs text-[var(--color-muted)]">
              Qty change
              <Input className="mt-1 max-w-xs font-mono" value={qtyDelta} onChange={(e) => setQtyDelta(e.target.value)}
                placeholder="e.g. 12 or -2"
                required
                disabled={busy}
              />
            </label>
            <label className="block text-xs text-[var(--color-muted)]">
              Unit cost
              {p.avgCost == null || Number(p.avgCost) === 0
                ? " (required for stock in)"
                : " (stock in; blank = avg)"}
              <Input className="mt-1 max-w-xs font-mono" value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                placeholder={
                  p.avgCost == null || Number(p.avgCost) === 0
                    ? "e.g. 20.00"
                    : p.avgCost
                }
                disabled={busy}
              />
            </label>
            <label className="block text-xs text-[var(--color-muted)]">
              Memo (optional)
              <Input className="mt-1 max-w-md" value={memo} onChange={(e) => setMemo(e.target.value)}
                maxLength={200}
                disabled={busy}
              />
            </label>
            {error ? (
              <Alert variant="destructive">{error}</Alert>
            ) : null}
            {status ? (
              <p className="m-0 text-sm text-[var(--color-muted)]">{status}</p>
            ) : null}
            <Button
              type="submit"
              pending={busy}
              pendingLabel="Posting…"
              disabled={!qtyDelta.trim()}
            >
              Post adjustment
            </Button>
          </form>
          {pendingChangeId && (
            <label className="mt-4 block text-xs text-[var(--color-muted)]">
              Emergency OTP from manager
              <div className="mt-1 flex gap-2">
                <Input className="min-w-0 max-w-xs flex-1 font-mono tracking-widest" inputMode="numeric" placeholder="••••••" maxLength={8} value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                  disabled={busy}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || otpCode.trim().length < 4}
                  onClick={() => {
                    setBusy(true);
                    setError(null);
                    void redeemOverrideOtp(companySlug!, {
                      code: otpCode.trim(),
                      requestId: pendingChangeId,
                      requestKind: "change",
                    })
                      .then(() => {
                        setPendingChangeId(null);
                        setOtpCode("");
                        setQtyDelta("");
                        setMemo("");
                        setStatus("OTP accepted — adjustment applied");
                        return reload();
                      })
                      .catch((err: unknown) =>
                        setError(
                          err instanceof Error ? err.message : "OTP failed",
                        ),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  Use
                </Button>
              </div>
            </label>
          )}
        </section>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="product"
        docId={productId}
        linkPath={`/c/${companySlug}/products/${productId}`}
        showFiles={false}
      />
    </PageFrame>
  );
}
